import React, { useState, useRef, useEffect } from 'react';
import './InputField.css';

interface InputFieldProps {
    type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'date';
    label?: string;
    value: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    required?: boolean;
    icon?: string;
    className?: string;
    backgroundColor?: string;
    size?: 'small' | 'medium' | 'large';
    width?: string;
    min?: string;
    max?: string;
}

const InputField: React.FC<InputFieldProps> = ({
    type = 'text',
    label,
    value,
    onChange,
    onFocus,
    placeholder,
    error,
    disabled = false,
    required = false,
    icon,
    className = '',
    backgroundColor = '#ffffff',
    size = 'medium',
    width,
    min,
    max
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const isFloating = Boolean(label); // Solo flotar si hay label

    const handleFocus = () => {
        setIsFocused(true);
        if (onFocus) onFocus();
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const getInputType = () => {
        if (type === 'password') {
            return showPassword ? 'text' : 'password';
        }
        return type;
    };

    return (
        <div className={`input-field-container ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${size}`} style={{ width: width || undefined }}>
            <div className="input-wrapper">
                {icon && (
                    <div className="input-icon">
                        <i className={`fas ${icon}`}></i>
                    </div>
                )}
                
                <input
                    ref={inputRef}
                    type={getInputType()}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    min={min}
                    max={max}
                    className={`input-field ${isFloating ? 'floating' : ''} ${icon ? 'has-icon' : ''}`}
                    style={{ backgroundColor }}
                />
                
                {label && (
                    <label 
                        className={`input-label ${isFloating ? 'floating' : ''}`}
                        style={{ backgroundColor }}
                    >
                        {label}
                        {required && <span className="required-asterisk">*</span>}
                    </label>
                )}

                {type === 'password' && (
                    <button
                        type="button"
                        className="password-toggle"
                        onClick={togglePasswordVisibility}
                        tabIndex={-1}
                    >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                )}
            </div>
            
            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

export default InputField;
